/**
 * 要件定義テーブルの行クリックで開く、1 名分の要件定義pt 明細モーダル。
 *
 * 表の列は「要件定義pt 12(3)」「待処理 8」までしか言わないので、**何の案件のどの成果物か**を
 * ここで開けるようにする。節は 処理済み（完了 / 処理中）と 待処理（未対応）に分かれる。
 *
 * データの出し方は節ごとに違う:
 *
 * - 処理済み（完了 / 処理中）: 開いたときに `/members/:email/develop-points` と
 *   `/wip-points` を引き、要件定義セット子票だけを残す（`buildMemberPtBreakdown` を
 *   当月pt 内訳カードと共有）。表の要件定義pt 列と同じ口径（`PT_EXCLUDED_MEMBERS` 除外・
 *   種別名の完全一致）なので、節の合計が列の値と一致する
 * - 待処理（未対応）: 表が既に持っている明細（`/combat-power/requirement-pending-resources`
 *   が列の値と一緒に、**母票単位に畳んだ形で**返したもの）をそのまま渡す。**同じ 1 レスポンス
 *   なので列の値と明細の合計が構造的にずれない**（別エンドポイントから引き直すと母票完了 /
 *   保留中の除外条件がずれて合計が食い違う）
 *
 * 取得は `open` のときだけ走らせる（全体ビューの表に 10 行以上並ぶので、開く前に
 * 全員分を引くと無駄な往復になる）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business エンジニア看板の要件定義テーブルの行クリックで開く 1 名分の要件定義pt 明細モーダル（処理済み=完了+処理中 / 待処理=未対応）。開いたときだけ完了・処理中明細を取得する
 * @graph-connects useMemberDevelopPoints [calls] 対象月の完了pt 明細フック
 * @graph-connects useMemberWipPoints [calls] 処理中pt 明細フック（現在スナップショット）
 * @graph-connects buildMemberPtBreakdown [calls] 明細行から要件定義セット子票だけを残す
 * @graph-connects RequirementPtDetailModal [calls] 明細モーダル本体（完了 / 処理中 / 未対応の 3 節）
 */

import { useMemo } from 'react';
import { useMemberDevelopPoints, useMemberWipPoints } from '../../../hooks/useMember.js';
import { monthEndOf } from '../../../lib/fiscal-quarter.js';
import type { RequirementPendingProject } from '../../../types/combat-power.js';
import { buildMemberPtBreakdown, type MemberPtRow } from '../member-pt-breakdown.js';
import { RequirementPtDetailModal } from '../RequirementPtDetailModal.js';
import { monthHeaderToYyyymm } from './combat-power-utils.js';

/** @graph-connects none */
interface RequirementMemberDetailModalProps {
  /** モーダル表示状態 */
  open: boolean;
  /** クローズ要求コールバック */
  onClose: () => void;
  /** 対象者のニックネーム（見出しに出す） */
  nickname: string;
  /** 対象者のメールアドレス（明細取得のキー） */
  email: string;
  /** 表示中の月ヘッダー（"2026-10月"）。完了明細の取得範囲と見出しの月に使う */
  monthHeader: string;
  /** 未対応（待処理）の明細。表が持っている母票単位の値をそのまま使う */
  pendingProjects: readonly RequirementPendingProject[];
}

/**
 * 明細取得の上限。当月pt 明細モーダルと同じ値（1 人 1 ヶ月の子票数は十分下回る）。
 * @graph-connects none
 */
const DETAIL_LIMIT = 500;

/** "2026-10月" → { monthStart: "2026-10-01", monthLabel: "2026/10" }。解釈できなければ null。 @graph-connects none */
function parseMonthHeader(monthHeader: string): { monthStart: string; monthLabel: string } | null {
  const ym = monthHeaderToYyyymm(monthHeader);
  if (ym === null) return null;
  const year = parseInt(ym.slice(0, 4), 10);
  const month = parseInt(ym.slice(5, 7), 10);
  return { monthStart: `${ym}-01`, monthLabel: `${String(year)}/${String(month)}` };
}

/** @graph-connects none */
export function RequirementMemberDetailModal({
  open,
  onClose,
  nickname,
  email,
  monthHeader,
  pendingProjects,
}: RequirementMemberDetailModalProps) {
  const month = parseMonthHeader(monthHeader);
  // 月ヘッダーが解釈できない場合も待処理（現在スナップショット）だけは出せるので、
  // 完了明細の取得だけを止める（email を null にするとフックが fetch しない）
  const detailEmail = open && month !== null ? email : null;

  const { data: completedData, isLoading: isCompletedLoading } = useMemberDevelopPoints(
    detailEmail,
    month === null
      ? null
      : { startDate: month.monthStart, endDate: monthEndOf(month.monthStart), limit: DETAIL_LIMIT },
  );
  const { data: wipData, isLoading: isWipLoading } = useMemberWipPoints(email, open);

  const requirementRows = useMemo(() => {
    const completedRows: MemberPtRow[] = (completedData?.data ?? []).map((d) => ({
      issueKey: d.issueKey,
      summary: d.summary,
      issueTypeName: d.issueTypeName,
      point: d.developPoint,
      completedDate: d.completedDate,
      parentIssueKey: d.parentIssueKey,
      parentSummary: d.parentSummary,
    }));
    const wipRows: MemberPtRow[] = (wipData?.data ?? []).map((d) => ({
      issueKey: d.issueKey,
      summary: d.summary,
      issueTypeName: d.issueTypeName,
      point: d.point,
      completedDate: null,
      parentIssueKey: d.parentIssueKey,
      parentSummary: d.parentSummary,
    }));
    return buildMemberPtBreakdown(completedRows, wipRows);
  }, [completedData, wipData]);

  return (
    <RequirementPtDetailModal
      open={open}
      onClose={onClose}
      nickname={nickname}
      monthLabel={month?.monthLabel ?? monthHeader}
      completedRows={requirementRows.requirementCompletedRows}
      wipRows={requirementRows.requirementWipRows}
      openProjects={pendingProjects}
      isLoading={isCompletedLoading || isWipLoading}
    />
  );
}
